<template>
  <component
    :is="props.as"
    :class="[
      'relative text-primary-100 after:bg-inherit after:absolute after:w-4 after:h-4',
      arrowPositionClasses,
      {
        'after:[clip-path:polygon(0_100%,100%_100%,0_0)]':
          arrowPosition !== 'inline-quote' && arrowPosition !== 'inline-quote-left',
        'after:[clip-path:polygon(0%_100%,0%_0%,100%_0%)] after:w-5 after:h-4':
          arrowPosition === 'inline-quote' || arrowPosition === 'inline-quote-left',
      },
      {
        'bg-theme-30': variant === 'theme',
        'bg-white': variant === 'default',
        'bg-theme-60 text-white text-header-6 lg:text-header-6-desktop font-bold': variant === 'domain',
      },
      {
        'p-4': size === 'sm',
        'pt-6 pb-[30px] px-4 lg:py-6 lg:px-8': size === 'md',
      },
    ]"
    data-component="SpeechBalloon">
    <slot />
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue'

export type SpeechBalloonArrowPosition =
  | 'top-left'
  | 'top-center-left'
  | 'top-center-right'
  | 'top-right'
  | 'right-top'
  | 'right-bottom'
  | 'bottom-right'
  | 'bottom-center-right'
  | 'bottom-center'
  | 'bottom-center-left'
  | 'bottom-left'
  | 'inline-quote'
  | 'inline-quote-left'
  | 'left-bottom'
  | 'left-top'

export type SpeechBalloonProps = {
  size?: 'sm' | 'md'
  variant: 'theme' | 'default' | 'domain'
  as?: 'blockquote' | 'div'
  arrowPosition: SpeechBalloonArrowPosition
}

const props = withDefaults(defineProps<SpeechBalloonProps>(), {
  size: 'md',
  as: 'div',
})

const arrowPositionClasses = computed(() => {
  switch (props.arrowPosition) {
    case 'top-left':
      return 'rounded-tr-lg rounded-bl-lg rounded-br-lg after:-top-[15px] after:left-0'
    case 'top-center-left':
      return 'rounded-2xl after:-top-[6px] after:left-[47.5px] after:rotate-135'
    case 'top-center-right':
      return 'rounded-2xl after:-top-[6px] after:right-[47.5px] after:rotate-135'
    case 'top-right':
      return 'rounded-tl-lg rounded-bl-lg rounded-br-lg after:-top-[15px] after:right-0 after:rotate-270'
    case 'right-top':
      return 'rounded-tl-lg rounded-bl-lg rounded-br-lg after:top-0 after:-right-[15px] after:rotate-90'
    case 'right-bottom':
      return 'rounded-tl-lg rounded-bl-lg rounded-tr-lg after:bottom-0 after:-right-[15px]'
    case 'bottom-right':
      return 'rounded-tl-lg rounded-bl-lg rounded-tr-lg after:-bottom-[15px] after:right-0 after:rotate-180'
    case 'bottom-center-right':
      return 'rounded-2xl after:-bottom-[6px] after:right-[47.5px] after:rotate-315'
    case 'bottom-center':
      return 'rounded-2xl after:-bottom-[6px] after:left-1/2 after:-translate-x-1/2 after:rotate-315'
    case 'bottom-center-left':
      return 'rounded-2xl after:-bottom-[6px] after:left-[47.5px] after:rotate-315'
    case 'bottom-left':
      return 'rounded-tl-lg rounded-tr-lg rounded-br-lg after:-bottom-[15px] after:left-0 after:rotate-90'
    case 'inline-quote':
      return 'rounded-2xl after:top-[100%] after:left-[4.2rem]'
    case 'inline-quote-left':
      return 'rounded-2xl after:top-[100%] after:left-[3.2rem]'
    case 'left-bottom':
      return 'rounded-tl-lg rounded-tr-lg rounded-br-lg after:bottom-0 after:-left-[15px] after:rotate-270'
    case 'left-top':
      return 'rounded-bl-lg rounded-tr-lg rounded-br-lg after:top-0 after:-left-[15px] after:rotate-180'
    default:
      return ''
  }
})
</script>
